import { RouterModule, PreloadAllModules, Routes } from '@angular/router'
import { NgModule } from '@angular/core'
import { OauthCallbackGuard } from './routes/oauth-callback/oauth-callback.guard'
import { AuthGuard, ProfGuard } from './core/_guards'
import { productionGuard } from './core/_guards/production.guard'
import { UserResolver } from './core/_resolvers/user.resolver'
import { AdminProfGuard } from './core/_guards/admin-prof.guard'
import { GroupAdminGuard } from './core/_guards/group-admin.guard'
import { GroupMeetingsFeatureGuard } from './core/_guards/group-meetings-feature.guard'

export const PAGE_NAME_USER_PROFILE = 'User Profile'
export const PAGE_NAME_ADMIN_USER_PROFILE = 'Admin - User Profile'
export const PAGE_NAME_USER_AVAILABILITY_LIST = 'User Availability'
export const PAGE_NAME_MY_PROFILE = 'My Profile'
export const PAGE_NAME_GROUP_MANAGEMENT = 'Group Management'
export const PAGE_NAME_ADMIN_CONSOLE = 'Admin Console'

export const routes: Routes = [
    {
        path: 'dashboard',
        loadComponent: () =>
            import('./routes/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [AuthGuard],
        data: { robots: 'noindex, nofollow', pageName: 'Calendar', threeColumnLayout: true },
    },
    {
        path: 'profile',
        loadComponent: () =>
            import('./routes/profile/my-profile-container/my-profile-container.component').then(
                (m) => m.MyProfileContainerComponent,
            ),
        canActivate: [ProfGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_MY_PROFILE,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'user/:id',
        loadComponent: () =>
            import('./routes/user/user-profile-route-wrapper/user-profile-route-wrapper.component').then(
                (m) => m.UserProfileRouteWrapperComponent,
            ),
        canActivate: [ProfGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_USER_PROFILE,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'user/:id/availability',
        loadComponent: () =>
            import('./routes/user/availability/user-availability.component').then(
                (m) => m.UserAvailabilityListComponent,
            ),
        canActivate: [ProfGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_USER_AVAILABILITY_LIST,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'admin/profile/:id',
        loadComponent: () =>
            import('./routes/user/user-profile-route-wrapper/user-profile-route-wrapper.component').then(
                (m) => m.UserProfileRouteWrapperComponent,
            ),
        canActivate: [AdminProfGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_ADMIN_USER_PROFILE,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'admin',
        loadComponent: () =>
            import('./routes/admin/admin-console.component').then((m) => m.AdminConsoleComponent),
        canActivate: [AdminProfGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_ADMIN_CONSOLE,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'group-management',
        loadComponent: () =>
            import('./routes/group-management/group-management.component').then(
                (m) => m.GroupManagementComponent,
            ),
        canActivate: [GroupAdminGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: PAGE_NAME_GROUP_MANAGEMENT,
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'account',
        loadChildren: () => import('./routes/account/account.module').then((m) => m.AccountModule),
    },
    {
        // old route, for compatibility with old links
        path: 'launch/:id',
        redirectTo: 'session/:id',
        pathMatch: 'full' as const,
    },
    {
        path: 'session/:id',
        loadComponent: () =>
            import('./routes/session/launch-container.component').then(
                (m) => m.LaunchContainerComponent,
            ),
        canActivate: [AuthGuard],
        resolve: { currentUser: UserResolver },
        data: { robots: 'noindex, nofollow' },
    },
    {
        path: 'session/g/:id',
        loadComponent: () =>
            import('./routes/session/group-session-launch-container.component').then(
                (m) => m.GroupSessionLaunchContainerComponent,
            ),
        canActivate: [AuthGuard],
        resolve: { currentUser: UserResolver },
        data: { robots: 'noindex, nofollow' },
    },
    {
        path: 'login',
        loadChildren: () => import('./routes/login/login.module').then((m) => m.LoginModule),
    },
    {
        path: 'signup',
        loadChildren: () => import('./routes/signup/signup.module').then((m) => m.SignupModule),
    },
    {
        path: 'oauth/authorize',
        loadChildren: () => import('./routes/oauth/oauth.module').then((m) => m.OAuthModule),
    },
    {
        path: 'oauthcallback/:provider',
        loadComponent: () =>
            import('./routes/oauth-callback/oauth-callback.component').then(
                (m) => m.OauthCallbackComponent,
            ),
        canActivate: [OauthCallbackGuard],
        data: { threeColumnLayout: true },
    },
    {
        path: 'sessions',
        loadComponent: () =>
            import('./routes/sessions/sessions.component').then((m) => m.SessionsComponent),
        canActivate: [AuthGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: 'My Sessions',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'blocked',
        loadComponent: () =>
            import('./routes/blocked/blocked.component').then((m) => m.BlockedComponent),
        canActivate: [AuthGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Blocked List',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'group-meetings',
        loadComponent: () =>
            import('./routes/group-meetings/group-meetings.component').then(
                (m) => m.GroupMeetingsComponent,
            ),
        canActivate: [GroupMeetingsFeatureGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Group Meetings',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'people',
        loadComponent: () =>
            import('./routes/people/people-list.component').then((m) => m.PeopleListComponent),
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: 'People List',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    {
        path: 'invite',
        loadComponent: () =>
            import('./routes/invite/invite.component').then((m) => m.InviteComponent),
        data: { pageName: 'Invite', threeColumnLayout: true, hideBookingHubOnMobile: true },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        path: 'refer',
        loadComponent: () =>
            import('./routes/refer/rewards-referrals.component').then(
                (m) => m.RewardsReferralsComponent,
            ),
        data: { pageName: 'Refer', threeColumnLayout: true, hideBookingHubOnMobile: true },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        path: 'settings',
        loadComponent: () =>
            import('./routes/settings/settings.component').then((m) => m.SettingsComponent),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Settings',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        // old route, for compatibility with old links
        path: 'profile/edit-p',
        redirectTo: 'settings',
        pathMatch: 'full' as const,
    },
    {
        // old route, for compatibility with old links
        path: 'profile/edit-q',
        redirectTo: 'profile',
        pathMatch: 'full' as const,
    },
    {
        path: 'help',
        loadComponent: () =>
            import('./routes/help-and-resources/help-and-resources.component').then(
                (m) => m.HelpAndResourcesComponent,
            ),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Help & Resources',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        path: 'help/faq',
        loadComponent: () =>
            import('./routes/help-and-resources/faq/faq.component').then((m) => m.FaqComponent),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Common Questions',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        path: 'help/guidelines',
        loadComponent: () =>
            import('./routes/help-and-resources/guidelines/guidelines.component').then(
                (m) => m.GuidelinesComponent,
            ),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Community Guidelines',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: { currentUser: UserResolver },
        canActivate: [AuthGuard],
    },
    {
        path: 'i/:hash',
        loadChildren: () =>
            import('./user-availability/user-availability.module').then(
                (m) => m.UserAvailabilityModule,
            ),
    },
    {
        path: 'availability/favorites',
        loadComponent: () =>
            import('./routes/availability/favorites/favorites-list.component').then(
                (m) => m.FavoritesListComponent,
            ),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Availability: Favorites',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: {
            currentUser: UserResolver,
        },
        canActivate: [AuthGuard],
    },
    {
        path: 'tasks/completed',
        loadComponent: () =>
            import('./routes/tasks/task-manager-completed-page.component').then(
                (m) => m.TaskManagerCompletedPageComponent,
            ),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Completed Tasks',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: {
            currentUser: UserResolver,
        },
        canActivate: [AuthGuard],
    },
    {
        path: 'tasks',
        loadComponent: () =>
            import('./routes/tasks/task-manager-master-list.component').then(
                (m) => m.TaskManagerMasterListComponent,
            ),
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Task Manager',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
        resolve: {
            currentUser: UserResolver,
        },
        canActivate: [AuthGuard],
    },
    {
        path: 'upgrade',
        loadChildren: () => import('./routes/upgrade/upgrade.routes').then((m) => m.UPGRADE_ROUTES),
    },
    {
        path: 'theme',
        loadChildren: () => import('./routes/theme/theme.routes').then((m) => m.THEME_ROUTES),
        canActivate: [productionGuard],
        data: {
            robots: 'noindex, nofollow',
            pageName: 'Theme',
            threeColumnLayout: true,
            hideBookingHubOnMobile: true,
        },
    },
    { path: '**', redirectTo: 'dashboard' },
]

@NgModule({
    imports: [
        RouterModule.forRoot(routes, {
            preloadingStrategy: PreloadAllModules,
            enableTracing: false,
            anchorScrolling: 'enabled',
            onSameUrlNavigation: 'reload',
            scrollPositionRestoration: 'enabled',
            paramsInheritanceStrategy: 'always',
        }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
